import { Enums, metaData } from '@cornerstonejs/core';
import { Enums as MetadataEnums, utilities, logging, } from '@cornerstonejs/metadata';
import createImage from '../createImage.js';
import { xhrRequest } from '../internal/index.js';
import dataSetCacheManager from './dataSetCacheManager.js';
import getPixelData from './getPixelData.js';
import loadFileRequest from './loadFileRequest.js';
import parseImageId from './parseImageId.js';
const { ImageQualityStatus } = Enums;
const log = logging.loaderLog.getLogger('wadouri');
const { addDicomPart10Instance } = utilities;
const NATURALIZED = MetadataEnums.MetadataModules.NATURALIZED;
function addDecache(imageLoadObject, imageId) {
    imageLoadObject.decache = function () {
        const parsedImageId = parseImageId(imageId);
        dataSetCacheManager.unload(parsedImageId.url);
    };
}
function loadImageFromPromise(dataSetPromise, imageId, frame = 0, sharedCacheKey, options, callbacks) {
    const start = new Date().getTime();
    const imageLoadObject = {
        cancelFn: undefined,
        promise: undefined,
    };
    imageLoadObject.promise = new Promise((resolve, reject) => {
        dataSetPromise.then((dataSet) => {
            const pixelData = getPixelData(dataSet, frame);
            const transferSyntax = dataSet.string('x00020010');
            const loadEnd = new Date().getTime();
            const imagePromise = createImage(imageId, pixelData, transferSyntax, options);
            addDecache(imageLoadObject, imageId);
            imagePromise.then((image) => {
                image = image;
                image.data = dataSet;
                const end = new Date().getTime();
                image.loadTimeInMS = loadEnd - start;
                image.totalTimeInMS = end - start;
                image.imageQualityStatus = ImageQualityStatus.FULL_RESOLUTION;
                if (callbacks !== undefined &&
                    callbacks.imageDoneCallback !== undefined) {
                    callbacks.imageDoneCallback(image);
                }
                resolve(image);
            }, function (error) {
                reject({
                    error,
                    dataSet,
                });
            });
        }, function (error) {
            reject({
                error,
            });
        });
    });
    return imageLoadObject;
}
function loadImageFromDataSet(dataSet, imageId, frame = 0, _sharedCacheKey, options) {
    const start = new Date().getTime();
    const promise = new Promise((resolve, reject) => {
        const loadEnd = new Date().getTime();
        let imagePromise;
        try {
            const pixelData = getPixelData(dataSet, frame);
            const transferSyntax = dataSet.string('x00020010');
            imagePromise = createImage(imageId, pixelData, transferSyntax, options);
        }
        catch (error) {
            reject({
                error,
                dataSet,
            });
            return;
        }
        imagePromise.then((image) => {
            image = image;
            image.data = dataSet;
            const end = new Date().getTime();
            image.loadTimeInMS = loadEnd - start;
            image.totalTimeInMS = end - start;
            image.imageQualityStatus = ImageQualityStatus.FULL_RESOLUTION;
            resolve(image);
        }, reject);
    });
    return {
        promise: promise,
        cancelFn: undefined,
    };
}
function getLoaderForScheme(scheme) {
    if (scheme === 'dicomweb' || scheme === 'wadouri') {
        return xhrRequest;
    }
    else if (scheme === 'dicomfile') {
        return loadFileRequest;
    }
}
const asByteArray = (data) => data instanceof ArrayBuffer ? new Uint8Array(data) : data;
function concatPixelData(pixelData) {
    if (!Array.isArray(pixelData)) {
        return asByteArray(pixelData);
    }
    if (pixelData.length === 0) {
        return undefined;
    }
    if (pixelData.length === 1) {
        return asByteArray(pixelData[0]);
    }
    let totalLength = 0;
    for (const frame of pixelData) {
        totalLength += asByteArray(frame).length;
    }
    const result = new Uint8Array(totalLength);
    let offset = 0;
    for (const frame of pixelData) {
        const view = asByteArray(frame);
        result.set(view, offset);
        offset += view.length;
    }
    return result;
}
function loadImageFromNaturalizedMetadata(imageId, options = {}) {
    const parsedImageId = parseImageId(imageId);
    options = Object.assign({}, options);
    delete options.loader;
    const schemeLoader = getLoaderForScheme(parsedImageId.scheme);
    const frameIndex = parsedImageId.pixelDataFrame !== undefined
        ? parsedImageId.pixelDataFrame
        : 0;
    const promise = (async () => {
        const start = Date.now();
        log.info('[dicomImageLoader/wadouri] loadImageFromNaturalizedMetadata: start', {
            imageId,
            scheme: parsedImageId.scheme,
            url: parsedImageId.url,
            frameIndex,
        });
        let natural = metaData.get(NATURALIZED, imageId);
        if (!natural) {
            log.info('[dicomImageLoader/wadouri] loadImageFromNaturalizedMetadata: no NATURALIZED metadata, attempting to fetch and populate', { imageId });
            if (!schemeLoader) {
                throw new Error(`loadImageFromNaturalizedMetadata: no NATURALIZED cache and unknown scheme ${parsedImageId.scheme}`);
            }
            const result = (await schemeLoader(parsedImageId.url, imageId));
            const arrayBuffer = result instanceof ArrayBuffer ? result : result.arrayBuffer;
            const baseImageId = `${parsedImageId.scheme}:${parsedImageId.url}`;
            await addDicomPart10Instance(baseImageId, arrayBuffer);
            natural = metaData.get(NATURALIZED, imageId);
        }
        const loadEnd = Date.now();
        const frameData = metaData.getTyped(MetadataEnums.MetadataModules.COMPRESSED_FRAME_DATA, imageId, { frameIndex });
        if (!frameData) {
            log.warn('[dicomImageLoader/wadouri] loadImageFromNaturalizedMetadata: no COMPRESSED_FRAME_DATA for imageId', { imageId, frameIndex });
            throw new Error(`loadImageFromNaturalizedMetadata: no pixel data in NATURALIZED for imageId ${imageId}`);
        }
        const { pixelData, transferSyntaxUid } = frameData;
        const concatenatedPixelData = concatPixelData(pixelData);
        const image = await createImage(imageId, concatenatedPixelData, transferSyntaxUid, options);
        const end = Date.now();
        const out = image;
        out.imageQualityStatus = ImageQualityStatus.FULL_RESOLUTION;
        out.data = natural;
        out.loadTimeInMS = loadEnd - start;
        out.totalTimeInMS = end - start;
        return out;
    })();
    return { promise };
}
const loadImage = (imageId, options = {}) => {
    const parsedImageId = parseImageId(imageId);
    const schemeLoader = getLoaderForScheme(parsedImageId.scheme);
    if (!schemeLoader) {
        throw new Error(`wadouri loadImage: no loader for scheme '${parsedImageId.scheme}'`);
    }
    const frameIndex = parsedImageId.pixelDataFrame !== undefined
        ? parsedImageId.pixelDataFrame
        : 0;
    const sharedCacheKey = parsedImageId.url;
    const dataSetPromise = dataSetCacheManager.load(parsedImageId.url, schemeLoader, imageId);
    return loadImageFromPromise(dataSetPromise, imageId, frameIndex, sharedCacheKey, options);
};
export { loadImageFromPromise, getLoaderForScheme, loadImage, loadImageFromNaturalizedMetadata, loadImageFromDataSet, };
